/** ----------------------------------------
    Imports
 ---------------------------------------- */

import { UserService } from '@store/service/UserService';
import { FileService } from '@store/service/FileService';
import Cookies from 'js-cookie';
import router from '@router/router';
import UserJob from '@job/UserJob';
import AppJob from '@job/AppJob';

/** ----------------------------------------
    Actions
---------------------------------------- */

const actions = {

    /** ----------------------------------------
        Login
    ---------------------------------------- */

    async login({ commit }: any, payload: any) {
        let redirect = '/projects';

        if (payload.redirect) {
            redirect = payload.redirect;
            delete payload.redirect;
        }

        const userService = new UserService(this);
        return userService.postLogin(payload)
            .then(response => {
                if (response instanceof Error)
                    return response;

                // Set token
                commit('setToken', response.token);
                Cookies.set('token', response.token);

                // Retrieve user data
                const userJob = new UserJob();
                return userJob.init().then(response => {

                    // Redirect to dashboard
                    router.push(redirect);
                });
            });
    },

    /** ----------------------------------------
        Register
    ---------------------------------------- */

    async register({ commit }: any, payload: any) {
        const userService = new UserService(this);
        return userService.postRegister(payload)
            .then(response => {
                if (response instanceof Error)
                    return response;

                return this.dispatch('user/login', {
                    email: payload.email,
                    password: payload.password,
                    redirect: '/apply',
                });
            });
    },

    /** ----------------------------------------
        Update
    ---------------------------------------- */

    async update({ commit }: any, payload: any) {
        const userService = new UserService(this);
        return userService.putUpdate(payload.id, payload).then(response => {
            if (response instanceof Error) {
                return response;
            }

            commit('setData', response.data);
        });
    },

    /** ----------------------------------------
        Invite
    ---------------------------------------- */

    async invite({ commit }: any, payload: any) {
        const userService = new UserService(this);
        return userService.postInvite(payload)
            .then(response => {
                if (response instanceof Error)
                    return response;

                return true;
            });
    },

    /** ----------------------------------------
        Forgot
    ---------------------------------------- */

    async forgot({ commit }: any, payload: any) {
        const userService = new UserService(this);
        return userService.postForgot(payload);
    },

    /** ----------------------------------------
        Reset
    ---------------------------------------- */

    async reset({ commit }: any, payload: any) {
        const userService = new UserService(this);
        return userService.postReset(payload);
    },

    /** ----------------------------------------
        Logout user
    ---------------------------------------- */

    async logout({ commit }: any) {
        const userService = new UserService(this);

        try {
            return await userService.getLogout().then(response => {
                this.dispatch('user/removeToken');
                this.dispatch('clearStore');
                router.push('/login');
            });
        } catch(error) {
            this.dispatch('user/removeToken');
            this.dispatch('clearStore');
            router.push('/login');
        }
    },

    /** ----------------------------------------
        Fetch user
    ---------------------------------------- */

    async fetch({ commit }: any) {
        const userService = new UserService(this);
        return userService.getUserData().then(response => {
            if (response instanceof Error) {
                this.dispatch('user/removeToken');
                location.reload();
            }

            commit('setData', response.data);

            const appJob = new AppJob(this._vm);

            return appJob.init()
                .then(response => {
                    if (response instanceof Error)
                        return response;

                    return true;
                });
        });
    },

    /** ----------------------------------------
        Fetch user picture
    ---------------------------------------- */

    async fetchPicture({ commit }: any) {
        this.fileService = new FileService(this);
        return await this.fileService.getFile(this.state.user.data.id, 'FrontendUser', 'picture').then(response => {
            if (response instanceof Error || !response.data[0])
                return true;

            const file = response.data[0];

            return this.fileService.downloadFile(file.id).then(res => {
                const fileName = (responseFile.filename) ? responseFile.filename : responseFile.type;
                const picture = {
                    name: `${ fileName }.${ file.extension }`,
                    id: file.id,
                    disabled: false,
                    blob: res,
                    href: window.URL.createObjectURL(res),
                }
                commit('setPicture', picture);
                return true;
            });
        });
    },

    /** ----------------------------------------
        Fetch user picture
    ---------------------------------------- */

    async setPicture({ commit }: any, payload: any) {
        commit('setPicture', payload);
    },

    /** ----------------------------------------
        Fetch user projects
    ---------------------------------------- */

    async fetchProjects({ commit }: any) {
        const userService = new UserService(this);
        return userService.getUserProjects().then(response => {
            if (response instanceof Error)
                return response;

            commit('setProjects', response.data);
        });
    },

    /** ----------------------------------------
        Fetch user projects
    ---------------------------------------- */

    async fetchOrganisations({ commit }: any) {
        const userService = new UserService(this);
        return userService.getUserOrganisations().then(response => {
            if (response instanceof Error)
                return response;

            commit('setOrganisations', response.data);
        });
    },

    /** ----------------------------------------
        Remove user token
    ---------------------------------------- */

    removeToken({ commit }: any) {
        commit('setToken', '');
        Cookies.remove('token');
    },
};

/** ----------------------------------------
    Export Actions
---------------------------------------- */

export default actions;